<template>
  <div>
    <table
      :class="{
        ['v-data-table']: true,
        [tableType]: true,
        [layout]: true,
        ['no-border']: !bordered,
        [extraClass]: !!extraClass
      }"
    >
      <thead>
        <slot name="thead">
          <tr v-if="headers">
            <th
              v-for="(header, idx) in headers"
              :key="idx"
              :class="{
                [header.extraClass]: !!header.extraClass,
                sortable: header.sortable,
                active: currentSort && header.key === currentSort.key
              }"
              v-on="
                header.sortable && rows.length
                  ? { click: () => onSort(header.key) }
                  : {}
              "
              @mouseenter="hoveredTh = header.key"
              @mouseleave="hoveredTh = null"
              :style="{
                width: header.width ? header.width : 'auto',
                maxWidth: header.width ? header.width : 'auto'
              }"
            >
              <div class="header__wrapper">
                <span
                  v-if="header.display !== undefined ? header.display : true"
                >
                  {{ header.label }}
                </span>
                <span
                  v-if="
                    header.sortable &&
                      (header.display !== undefined ? header.display : true)
                  "
                  class="v-table__sort-icon"
                  ><img
                    :src="getSortIcon(header.key, header.sortType)"
                    alt="sortIcon"
                  />
                </span>
                <component
                  v-if="header.component && header.display"
                  :is="header.component"
                  v-bind="header.props"
                ></component>
              </div>
            </th>
          </tr>
        </slot>
      </thead>
      <tbody>
        <slot name="tbody">
          <template v-if="rows">
            <tr
              :class="{
                ['clickable']: row.click,
                [classType]: true,
                [row.extraClass]: !!row.extraClass
              }"
              v-for="row in rows"
              :key="row.key"
              @click="getClickFunc(row)"
            >
              <td
                v-for="v in row.values"
                v-if="row.display !== undefined ? row.display : true"
                :key="v.key"
                :class="{
                  ['clickable']: v.click,
                  [v.extraClass]: !!v.extraClass,
                  ['link']: !!v.link
                }"
                @click="getClickFunc(v)"
              >
                <component
                  v-if="v.component && !v.hidden"
                  :is="v.component"
                  v-bind="v.props"
                ></component>
                <span
                  v-on="v.click ? { click: v.click } : {}"
                  v-else-if="!v.hidden"
                >
                  {{ v.value }}
                </span>
              </td>
            </tr>
          </template>
        </slot>
      </tbody>
    </table>
    <div v-if="!hidePlaceholder && !rows.length" class="empty-table">
      no data
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

// icons
import az from "@/assets/images/sorting/a-z.svg";
import za from "@/assets/images/sorting/z-a.svg";
import bs from "@/assets/images/sorting/b-s.svg";
import sb from "@/assets/images/sorting/s-b.svg";

import azActive from "@/assets/images/sorting/a-z-active.svg";
import zaActive from "@/assets/images/sorting/z-a-active.svg";
import bsActive from "@/assets/images/sorting/b-s-active.svg";
import sbActive from "@/assets/images/sorting/s-b-active.svg";

export default Vue.component("VDataTable", {
  props: {
    infiniteScrollDisabled: VueTypes.bool.def(false),
    headers: VueTypes.arrayOf(
      VueTypes.shape({
        label: VueTypes.string.isRequired,
        key: VueTypes.string.isRequired,
        sortable: VueTypes.bool.def(false),
        sortType: VueTypes.oneOf(["number", "string"]).def("number"),
        extraClass: VueTypes.string.def(""),
        display: VueTypes.bool.def(true),
        width: VueTypes.string,
        component: VueTypes.func
      })
    ).def([]),
    rows: VueTypes.arrayOf(VueTypes.object).def([]),
    bordered: VueTypes.bool.def(false),
    extraClass: VueTypes.string.def(""),
    currentSort: VueTypes.shape({
      key: VueTypes.string.isRequired,
      value: VueTypes.bool.isRequired
    }).def({}),
    classType: VueTypes.oneOf(["primary", "secondary", "tertiary", "dark"]).def(
      "dark"
    ),
    tableType: VueTypes.oneOf(["preview", "text"]).def("preview"),
    layout: VueTypes.oneOf(["fixed", "auto"]).def("auto"),

    // hide "NO DATA" message if no rows given
    hidePlaceholder: VueTypes.bool.def(false)
  },

  data() {
    return {
      hoveredTh: null,

      activeSortIcons: {
        az: azActive,
        za: zaActive,
        bs: bsActive,
        sb: sbActive
      },

      sortIcons: {
        az,
        za,
        bs,
        sb
      }
    };
  },

  methods: {
    getClickFunc(tag) {
      return tag.click ? tag.click() : () => {};
    },

    getSortIcon(key, sortType) {
      let base = (base = sortType === "string" ? "za" : "bs");

      if (
        this.currentSort &&
        key === this.currentSort.key &&
        this.currentSort.value
      ) {
        base = sortType === "string" ? "az" : "sb";
      }

      if (this.hoveredTh === key) {
        return this.activeSortIcons[base];
      }

      return this.sortIcons[base];
    },

    onSort(key) {
      this.$emit("sort", {
        key,
        value: this.currentSort.key === key ? !this.currentSort.value : true,
        order: this.currentSort.key === key ? +!this.currentSort.order : 1
      });
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-data-table";

.#{$ns} {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  thead,
  tbody {
    tr {
      td {
        vertical-align: middle;
      }

      th,
      td {
        height: 60px;
        font-size: 15px;
        white-space: nowrap;
      }
    }
  }

  thead {
    tr {
      background: transparent;
      /* make last element in header  */
      /* align to left */
      th:last-of-type {
        .header__wrapper {
          justify-content: flex-end;
        }
      }

      th {
        transition: color 0.2s linear;
        color: rgba($primary-color, 0.6);
        font-weight: 400;
        text-align: left;
        font-family: Lato-Bold, sans-serif;
        vertical-align: middle;
        cursor: default;

        &.sortable {
          cursor: pointer;

          &:hover {
            color: $primary-color;
          }
        }
      }
    }
  }

  tbody {
    tr {
      color: $button-dark-color;

      &:hover {
        background-color: #f5f7f9;
      }

      &.clickable {
        cursor: pointer;
      }
    }

    td {
      color: $secondary-text-color;

      border-top: 1px solid $primary-grey;

      &.clickable {
        cursor: pointer;
      }

      &.link {
        &:hover {
          color: $primary-color;
        }
      }

      span {
        line-height: 1.2em;
      }
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }

  &.fixed {
    table-layout: fixed;
  }

  &.preview {
  }

  &.text {
    thead,
    tbody {
      tr {
        th,
        td {
          height: 60px;
        }
      }
    }
  }

  .v-table__sort-icon {
    cursor: pointer;
    padding-left: 5px;

    i {
      color: inherit;
    }

    &.active {
      @include mainColorPaletteClasses(1);
    }
  }
}

.header__wrapper {
  display: flex;
  align-items: center;
}

.empty-table {
  padding-top: 30px;
  font-family: Lato-Bold, Arial, sans-serif;
  font-size: 26px;
  line-height: 31px;
  text-align: center;
  text-transform: capitalize;
  color: $empty-grey;
  border-top: 1px solid $primary-grey;
}
</style>
